import React from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import { ChevronDownIcon } from '@mote/assets/icons'
import { ariaAttr } from '@mote/helpers'

const SelectField = React.forwardRef((props, ref) => {
  const { children, placeholder, ...rest } = props

  return (
    <StyledSelectField ref={ref} {...rest}>
      {placeholder && <option value=''>{placeholder}</option>}
      {children}
    </StyledSelectField>
  )
})

export const Select = React.forwardRef((props, ref) => {
  const {
    className,
    placeholder,
    currentValue = '',
    options: _options,
    disabled = false,
    invalid = false,
    required = false,
    readOnly = false,
    ...rest
  } = props

  const options = React.useMemo(
    () =>
      _options.map(({ value, text, disabled }, index) => (
        <option
          key={`select-option--${index}:${text}:${value}`}
          value={value}
          disabled={disabled}
        >
          {text}
        </option>
      )),
    [_options]
  )

  return (
    <Root disabled={disabled}>
      <SelectField
        {...rest}
        ref={ref}
        className={className}
        placeholder={placeholder}
        invalid={invalid}
        required={required}
        readOnly={readOnly}
        value={currentValue}
        aria-invalid={ariaAttr(invalid)}
        aria-required={ariaAttr(required)}
        aria-readonly={ariaAttr(readOnly)}
        aria-placeholder={ariaAttr(placeholder)}
      >
        {options}
      </SelectField>
      <ChevronIconContainer>
        <ChevronDownIcon width='0.65em' height='0.65em' color='currentColor' />
      </ChevronIconContainer>
    </Root>
  )
})

Select.propTypes = {
  currentValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  options: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool
}

const Root = styled.div`
  width: 100%;
  height: fit-content;
  position: relative;
  color: #25252d;

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
      cursor: not-allowed;
    `}
`

const StyledSelectField = styled.select`
  position: relative;
  height: 40px;
  width: 100%;
  min-width: 0px;
  padding: 0 32px 0 16px;
  outline: none;
  outline-offset: 2px;
  appearance: none;
  font-weight: normal;
  font-size: 15px;
  line-height: 100%;
  color: inherit;
  border-radius: 4px;
  border: 1px solid #d1d1e0;
  background: #ffffff;
  transition-duration: 150ms;
  transition-property: background-color, border-color, color, fill, stroke,
    opacity, box-shadow, transform;

  &:hover {
    border-color: #b2b2cc;
  }

  &:active,
  &:focus {
    z-index: unset;
    outline: none;
    border-color: #ac0ae8;
    box-shadow: 0 0 0 1px #ac0ae8;
  }

  ${({ invalid, disabled }) =>
    !disabled &&
    invalid &&
    css`
      border-color: #f44336;

      &:hover {
        border-color: #f44336;
      }

      &:active,
      &:focus {
        outline: none;
        border-color: #f44336;
        box-shadow: 0 0 0 1px #f44336;
      }
    `};
`

const ChevronIconContainer = styled.div`
  position: absolute;
  width: 24px;
  height: 100%;
  top: 50%;
  right: 8px;
  color: currentcolor;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  user-select: none;
  transform: translateY(-50%);
`
